import React, { useEffect, useMemo, useState } from "react";
import { Form, Badge, Card, Button, ListGroup } from "react-bootstrap";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import api from "../../api";
import EmployeePicker from "../shared/EmployeePicker";
import "./DynamicFormRenderer.css";

// Phase 3A built this as PREVIEW MODE ONLY — values were component-local and
// disposable. Phase 3B adds a second mode, "fill" (real assignment filling),
// WITHOUT touching preview's behavior: every fill-only prop defaults to
// undefined/no-op, and when `mode !== "fill"` the component falls back to
// its original internal-state behavior exactly as before. Same rendering
// logic, same field-type switch, same conditional-visibility evaluator —
// the backend's services/formConditionalLogicService.js mirrors this
// exact evaluateCondition()/isFieldVisible() pair so preview UX and the
// authoritative submit-time check never disagree about what "visible"
// means.
function evaluateCondition(condition, values) {
  const actual = values[condition.field_key];
  const expected = condition.value;

  switch (condition.operator) {
    case "EQUALS": return String(actual ?? "") === String(expected ?? "");
    case "NOT_EQUALS": return String(actual ?? "") !== String(expected ?? "");
    case "IN": return Array.isArray(expected) && expected.map(String).includes(String(actual ?? ""));
    case "NOT_IN": return Array.isArray(expected) && !expected.map(String).includes(String(actual ?? ""));
    case "IS_EMPTY": return actual === undefined || actual === null || actual === "";
    case "IS_NOT_EMPTY": return !(actual === undefined || actual === null || actual === "");
    case "GREATER_THAN": return Number(actual) > Number(expected);
    case "LESS_THAN": return Number(actual) < Number(expected);
    case "CONTAINS": return String(actual ?? "").includes(String(expected ?? ""));
    default: return true;
  }
}

// Exported so FormFillContent.jsx's pre-submit required-field check can
// reuse this EXACT same visibility rule (never a second, drifting copy) --
// a field hidden by the current answers must never be treated as a missing
// required answer, matching the backend's own formConditionalLogicService.
export function isFieldVisible(field, values) {
  let config = field.conditional_visibility_json;
  if (!config) return true;
  if (typeof config === "string") {
    try { config = JSON.parse(config); } catch { return true; }
  }
  if (!config.conditions?.length) return true;

  const results = config.conditions.map((c) => evaluateCondition(c, values));
  return config.logic === "OR" ? results.some(Boolean) : results.every(Boolean);
}

// Phase 3D §30 — result-status badge coloring, driven purely by the
// execution profile's own option codes (never a hardcoded label match).
const RESULT_BADGE_VARIANTS = {
  PASS: "success",
  FAIL: "danger",
  BLOCKED: "warning",
  NOT_APPLICABLE: "secondary",
  NOT_EXECUTED: "light",
};

function ResultStatusBadge({ code }) {
  if (!code) return <Badge bg={RESULT_BADGE_VARIANTS.NOT_EXECUTED} text="dark">Not Executed</Badge>;
  const variant = RESULT_BADGE_VARIANTS[code] || "light";
  return <Badge bg={variant} text={variant === "light" || variant === "warning" ? "dark" : undefined}>{code.replace("_", " ")}</Badge>;
}

// Live, client-side-only counts for the UAT primary section — purely a
// fill-time convenience; the authoritative summary is always recalculated
// server-side from committed row values after submit (formTestExecutionService).
function ExecutionLiveSummary({ rows, resultFieldKey }) {
  const counts = { PASS: 0, FAIL: 0, BLOCKED: 0, NOT_APPLICABLE: 0, remaining: 0 };
  (rows || []).forEach((row) => {
    const code = resultFieldKey ? String((row.values || {})[resultFieldKey] || "").toUpperCase() : "";
    if (counts[code] !== undefined) counts[code] += 1;
    else counts.remaining += 1;
  });
  const total = (rows || []).length;
  return (
    <div className="d-flex flex-wrap gap-2 mb-2 small">
      <Badge bg="light" text="dark">Total: {total}</Badge>
      <Badge bg="success">Passed: {counts.PASS}</Badge>
      <Badge bg="danger">Failed: {counts.FAIL}</Badge>
      <Badge bg="warning" text="dark">Blocked: {counts.BLOCKED}</Badge>
      <Badge bg="secondary">N/A: {counts.NOT_APPLICABLE}</Badge>
      <Badge bg="light" text="dark">Remaining: {counts.remaining}</Badge>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Real evidence-upload UI for fill mode — existing files (name/size, plus
// view/download/delete callbacks the parent wires to the secure
// /api/form-submissions/:id/files endpoints) and a native file input for
// new uploads. In preview mode this renders nothing functional, exactly as
// Phase 3A left it.
function FileFieldInput({ field, files, mode, readOnly, onUploadFile, onDeleteFile, onViewFile }) {
  if (mode !== "fill") {
    return <div className="border rounded p-2 text-muted small">File upload — not available in this phase (structure preview only).</div>;
  }

  const list = files || [];
  const accept = field.field_type === "IMAGE_UPLOAD" ? "image/png,image/jpeg" : ".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt";

  return (
    <div>
      {list.length > 0 && (
        <ListGroup className="mb-2">
          {list.map((f) => (
            <ListGroup.Item key={f.id} className="d-flex justify-content-between align-items-center py-1 px-2">
              <span className="small text-truncate me-2" style={{ maxWidth: "60%" }} title={f.original_name}>
                {f.original_name} <span className="text-muted">({formatBytes(f.file_size)})</span>
              </span>
              <span>
                {onViewFile && (
                  <Button size="sm" variant="link" className="py-0" onClick={() => onViewFile(f)}>View</Button>
                )}
                {!readOnly && onDeleteFile && (
                  <Button size="sm" variant="link" className="py-0 text-danger" onClick={() => onDeleteFile(f)}>Remove</Button>
                )}
              </span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {!readOnly && onUploadFile && (
        <Form.Control
          type="file"
          size="sm"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            if (file) onUploadFile(file);
            e.target.value = "";
          }}
        />
      )}
    </div>
  );
}

// RATING's minimum_value..maximum_value click-to-rate star control. The
// field's own configured range is the ONLY source of truth for how many
// stars render (never a hardcoded scale, never options.length) -- a stale
// legacy form_field_options row set (e.g. a leftover 1..10 label set from
// before an admin narrowed the field to 1..5) must never re-widen the
// control back out. Options are used ONLY to look up a display label for a
// given rating value (option_code String(value) -> option_label, e.g.
// 1=Very Poor..5=Excellent) -- they never affect star COUNT.
function RatingInput({ field, value, onChange, disabled, isInvalid }) {
  const [hoveredValue, setHoveredValue] = useState(null);
  const options = field.options || [];

  const minRaw = Number(field.minimum_value);
  const min = Number.isFinite(minRaw) ? minRaw : 1;
  const maxRaw = Number(field.maximum_value);
  // options.length is only a fallback for the rare field with no configured
  // maximum_value at all -- once maximum_value is set, it always wins.
  const max = Number.isFinite(maxRaw) ? maxRaw : (options.length || 5);
  const ratingValues = max > min ? Array.from({ length: max - min + 1 }, (_, i) => min + i) : [min];

  const hasValue = value !== undefined && value !== null && value !== "";
  const currentValue = hoveredValue ?? (hasValue ? Number(value) : null);

  const labelFor = (v) => options.find((o) => String(o.option_code) === String(v))?.option_label;
  const currentLabel = currentValue != null ? labelFor(currentValue) : null;
  // Permanent low/high scale caption (e.g. "1 Very Poor" ... "5 Excellent"),
  // sourced from the field's own configured options -- never invented text.
  // Purely a display addition alongside the existing hover/selected label.
  const lowLabel = labelFor(min);
  const highLabel = labelFor(max);

  return (
    <div className={isInvalid ? "is-invalid" : undefined}>
      <div className="d-flex align-items-center gap-1 dfr-rating-stars" role="radiogroup" aria-label={field.label}>
        {ratingValues.map((v) => (
          <span
            key={v}
            role="radio"
            aria-checked={hasValue && Number(value) === v}
            aria-label={labelFor(v) || `${v} star${v > 1 ? "s" : ""}`}
            tabIndex={disabled ? -1 : 0}
            className="form-rating-star"
            style={{ cursor: disabled ? "default" : "pointer", color: currentValue != null && v <= currentValue ? "#6d1530" : "#e7ded0" }}
            onMouseEnter={() => !disabled && setHoveredValue(v)}
            onMouseLeave={() => !disabled && setHoveredValue(null)}
            onClick={() => !disabled && onChange(String(v))}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(String(v)); }
            }}
          >
            {currentValue != null && v <= currentValue ? <StarIcon fontSize="medium" /> : <StarBorderIcon fontSize="medium" />}
          </span>
        ))}
        {currentLabel && <span className="small text-muted ms-2">{currentLabel}</span>}
      </div>
      {(lowLabel || highLabel) && (
        <div className="dfr-rating-scale">
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}

// PROJECT_SELECT — real project picker, scoped to active/accessible
// projects only via the existing GET /projects list (which already applies
// the caller's own data-scope filtering server-side — this component never
// adds its own filtering on top). Value submitted/stored is the project's
// numeric id, exactly like every other reference-select field.
function ProjectSelectInput({ value, onChange, disabled, isInvalid }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/projects", { params: { is_active: 1, pageSize: 200 } })
      .then((res) => { if (!cancelled) setProjects(Array.isArray(res.data?.data) ? res.data.data : []); })
      .catch(() => { if (!cancelled) setProjects([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Form.Select value={value || ""} onChange={(e) => onChange(e.target.value || null)} disabled={disabled || loading} isInvalid={isInvalid}>
      <option value="">{loading ? "Loading projects..." : "Select a project..."}</option>
      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </Form.Select>
  );
}

// PROJECT_MODULE_SELECT — disabled until a project is selected (via
// `projectId`, resolved by the caller from the sibling PROJECT_SELECT
// field's own value — there is no dependency config in the schema, so this
// is the minimal real hookup rather than an invented config format).
// Re-fetches (and the caller clears the stored value) whenever projectId
// changes, so a stale module from a previously-selected project can never
// be submitted against the new one.
function ProjectModuleSelectInput({ value, onChange, projectId, disabled, isInvalid }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevProjectIdRef = React.useRef(projectId);

  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      if (value) onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!projectId) { setModules([]); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } })
      .then((res) => { if (!cancelled) setModules(Array.isArray(res.data?.data) ? res.data.data : []); })
      .catch(() => { if (!cancelled) setModules([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (!projectId) {
    return <Form.Select disabled value=""><option value="">Select a project first...</option></Form.Select>;
  }

  return (
    <Form.Select value={value || ""} onChange={(e) => onChange(e.target.value || null)} disabled={disabled || loading} isInvalid={isInvalid}>
      <option value="">{loading ? "Loading modules..." : "Select a module..."}</option>
      {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
    </Form.Select>
  );
}

// Knowledge Sharing subject-selection hardening (requirements 1/2/4/5/6):
// the ONLY EMPLOYEE_SELECT case that gets a real, searchable picker with
// enough identity context to tell two similarly-named accounts apart (real
// ticket #106/#18: "Ratana" vs "Ms. Ratana Dubey", two distinct live
// accounts) -- scoped specifically to a field flagged is_subject_field, the
// "who is this submission ABOUT" field a KPI resolver actually keys off. A
// non-subject EMPLOYEE_SELECT/TASK_SELECT field is untouched (still the
// original plain text control below) -- out of scope for this hardening.
//
// Two distinct render paths, mutually exclusive:
//   - `subjectContext.locked` true (form_assignments.locked_subject_user_id
//     is set): read-only, always -- never re-enabled, never re-searched
//     (the assigner already decided who this is about; the value was seeded
//     server-side at submission-start and re-asserted on every draft-save,
//     see services/formSubmissionValueService.js#applyDraftPayload).
//   - otherwise: the SAME searchable EmployeePicker used by the Form
//     Builder's own subject-option editor, scoped to THIS submission's own
//     new subject-options search endpoint (active employees only, server-
//     side) -- requirement 6's "retain the current subject-field fallback",
//     just upgraded from a raw numeric-id text box to a real picker.
function EmployeeSubjectSelectInput({ value, onChange, disabled, isInvalid, subjectContext }) {
  const locked = !!subjectContext?.locked;
  const submissionId = subjectContext?.submissionId || null;

  if (locked) {
    const s = subjectContext?.subject;
    const label = s?.name
      ? `${s.name}${s.email ? ` (${s.email})` : ""}`
      : (value != null && value !== "" ? `User #${value}` : "Not yet assigned");
    return (
      <Form.Control
        readOnly
        disabled
        value={label}
        title="This field is locked to the employee this assignment was created for and cannot be changed."
        className={isInvalid ? "is-invalid" : undefined}
      />
    );
  }

  if (!submissionId) {
    // No fill-mode submission context available (e.g. preview mode, or a
    // caller that hasn't opted into subjectContext) -- fall back to the
    // original plain numeric-id input rather than wiring a picker with
    // nowhere to search.
    return <Form.Control value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} placeholder="Employee user id" />;
  }

  return (
    <EmployeePicker
      endpoint={`/form-submissions/${submissionId}/subject-options/search`}
      value={value || null}
      onChange={(userId) => onChange(userId != null ? String(userId) : null)}
      isDisabled={disabled}
      placeholder="Search employee by name, email, designation..."
    />
  );
}

function FieldInput({ field, value, onChange, mode, readOnly, files, onUploadFile, onDeleteFile, onViewFile, isInvalid, allValues, siblingFields, subjectContext }) {
  const options = field.options || [];
  const disabled = readOnly || !!field.is_read_only;

  switch (field.field_type) {
    case "SECTION_HEADING":
      return <h6 className="mt-2">{field.label}</h6>;
    case "STATIC_TEXT":
      return <p className="text-muted mb-1">{field.label}</p>;
    case "LONG_TEXT":
      return <Form.Control as="textarea" rows={3} placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "NUMBER":
    case "DECIMAL":
    case "SCORE":
    case "PERCENTAGE":
      return (
        <Form.Control
          type="number"
          placeholder={field.placeholder}
          value={value ?? ""}
          min={field.minimum_value ?? undefined}
          max={field.maximum_value ?? undefined}
          onChange={(e) => onChange(e.target.value)}
          readOnly={disabled}
          isInvalid={isInvalid}
        />
      );
    case "RATING":
      return <RatingInput field={field} value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} />;
    case "DATE":
      return <Form.Control type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "DATETIME":
      return <Form.Control type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "EMAIL":
      return <Form.Control type="email" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "MOBILE":
      return <Form.Control type="tel" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "URL":
      return <Form.Control type="url" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "DROPDOWN":
    case "RESULT_STATUS":
      return (
        <Form.Select value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} isInvalid={isInvalid}>
          <option value="">Select...</option>
          {options.map((o) => <option key={o.id || o.option_code} value={o.option_code}>{o.option_label}</option>)}
        </Form.Select>
      );
    case "RADIO":
    case "YES_NO": {
      const effectiveOptions = options.length ? options : [
        { option_code: "YES", option_label: "Yes" },
        { option_code: "NO", option_label: "No" },
      ];
      return (
        <div>
          {effectiveOptions.map((o) => (
            <Form.Check
              key={o.id || o.option_code}
              type="radio"
              name={field.field_key}
              label={o.option_label}
              checked={value === o.option_code}
              onChange={() => onChange(o.option_code)}
              disabled={disabled}
            />
          ))}
        </div>
      );
    }
    case "MULTI_SELECT":
    case "CHECKBOX": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          {options.map((o) => (
            <Form.Check
              key={o.id || o.option_code}
              type="checkbox"
              label={o.option_label}
              checked={selected.includes(o.option_code)}
              onChange={(e) => {
                const next = e.target.checked ? [...selected, o.option_code] : selected.filter((v) => v !== o.option_code);
                onChange(next);
              }}
              disabled={disabled}
            />
          ))}
        </div>
      );
    }
    case "FILE_UPLOAD":
    case "IMAGE_UPLOAD":
      return (
        <FileFieldInput
          field={field}
          files={files}
          mode={mode}
          readOnly={disabled}
          onUploadFile={onUploadFile}
          onDeleteFile={onDeleteFile}
          onViewFile={onViewFile}
        />
      );
    case "PROJECT_SELECT":
      return <ProjectSelectInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} />;
    case "PROJECT_MODULE_SELECT": {
      const projectField = (siblingFields || []).find((f) => f.field_type === "PROJECT_SELECT");
      const projectId = projectField ? (allValues || {})[projectField.field_key] : null;
      return <ProjectModuleSelectInput value={value} onChange={onChange} projectId={projectId} disabled={disabled} isInvalid={isInvalid} />;
    }
    case "EMPLOYEE_SELECT":
      if (field.is_subject_field) {
        return <EmployeeSubjectSelectInput value={value} onChange={onChange} disabled={disabled} isInvalid={isInvalid} subjectContext={subjectContext} />;
      }
      return <Form.Control placeholder={`${field.placeholder || "Search..."} ${mode === "fill" ? "" : "(picker not wired in preview)"}`} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "TASK_SELECT":
      return <Form.Control placeholder={`${field.placeholder || "Search..."} ${mode === "fill" ? "" : "(picker not wired in preview)"}`} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
    case "SIGNATURE_CONFIRMATION":
      return (
        <Form.Check
          type="checkbox"
          label={field.placeholder || "I confirm"}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
      );
    case "REPEATING_TABLE":
      return (
        <div className="border rounded p-2 text-muted small">
          Repeating table structure — real per-row data lives in this section's own repeatable rows above/below.
        </div>
      );
    case "CALCULATED": {
      let calc = field.calculation_config_json;
      if (typeof calc === "string") { try { calc = JSON.parse(calc); } catch { calc = null; } }
      return (
        <div className="border rounded p-2 text-muted small">
          Calculated field ({calc?.operation || "?"} of {(calc?.source_fields || []).join(", ")}) — preview-only, non-authoritative;
          server-side calculation is not part of this phase.
        </div>
      );
    }
    default:
      return <Form.Control placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={disabled} isInvalid={isInvalid} />;
  }
}

function FieldGroup({ field, value, onChange, mode, readOnly, files, onUploadFile, onDeleteFile, onViewFile, error, allValues, siblingFields, questionNumber, groupId, subjectContext }) {
  const isStatic = field.field_type === "SECTION_HEADING" || field.field_type === "STATIC_TEXT";
  return (
    <Form.Group id={groupId} className={`mb-3 dfr-field-group ${isStatic ? "" : "dfr-field-group-question"}`}>
      {!isStatic && (
        <Form.Label className="dfr-field-label">
          {questionNumber != null && <span className="dfr-q-number">{String(questionNumber).padStart(2, "0")}</span>}
          <span className="dfr-q-text">
            {field.label}
            {!!field.is_required && <span className="text-danger"> *</span>}
          </span>
          {!!field.score_enabled && <Badge className="ms-2 dfr-badge dfr-badge-scored">Scored</Badge>}
          {!field.is_required && !field.score_enabled && <Badge className="ms-2 dfr-badge dfr-badge-optional">Optional</Badge>}
        </Form.Label>
      )}
      <FieldInput
        field={field} value={value} onChange={onChange} mode={mode} readOnly={readOnly}
        files={files} onUploadFile={onUploadFile} onDeleteFile={onDeleteFile} onViewFile={onViewFile}
        isInvalid={!!error} allValues={allValues} siblingFields={siblingFields} subjectContext={subjectContext}
      />
      {field.help_text && <Form.Text className="text-muted d-block">{field.help_text}</Form.Text>}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </Form.Group>
  );
}

// One repeating-section's rows: add/remove bounded by minimum_repetitions/
// maximum_repetitions, each row an independent values map keyed by
// field_key — mirrors form_submission_row_values' per-row storage on the
// backend exactly (services/formSubmissionValueService.js).
function RepeatingSectionRows({ section, fields, rows, parentValues, mode, readOnly, filesByRow, onRowValueChange, onAddRow, onRemoveRow, onUploadRowFile, onDeleteFile, onViewFile, executionProfile }) {
  const activeRows = rows || [];
  const atMax = section.maximum_repetitions != null && activeRows.length >= Number(section.maximum_repetitions);
  const atMin = section.minimum_repetitions != null && activeRows.length <= Number(section.minimum_repetitions);

  const isExecutionSection = executionProfile && executionProfile.primary_repeatable_section_id === section.id;
  const resultField = isExecutionSection ? fields.find((f) => f.id === executionProfile.result_status_field_id) : null;
  const resultFieldKey = resultField?.field_key || null;

  if (mode !== "fill") {
    return (
      <div className="text-muted small mb-2">
        Repeating section (min {section.minimum_repetitions ?? 0}, max {section.maximum_repetitions ?? "∞"}) —
        row-level preview is not available in this phase.
      </div>
    );
  }

  return (
    <div className="mb-2">
      {isExecutionSection && resultFieldKey && <ExecutionLiveSummary rows={activeRows} resultFieldKey={resultFieldKey} />}
      {activeRows.length === 0 && <div className="text-muted small mb-2">No rows added yet.</div>}
      {activeRows.map((row, rowIndex) => (
        <Card key={row.id} className="mb-2 bg-light-subtle">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">
                {isExecutionSection ? "Test Case" : "Row"} {rowIndex + 1}
                {isExecutionSection && resultFieldKey && <span className="ms-2"><ResultStatusBadge code={String((row.values || {})[resultFieldKey] || "").toUpperCase() || null} /></span>}
              </strong>
              {!readOnly && (
                <Button size="sm" variant="outline-danger" disabled={atMin} onClick={() => onRemoveRow(row.id)}>Remove row</Button>
              )}
            </div>
            {fields.map((field) => {
              const rowValues = { ...(parentValues || {}), ...(row.values || {}) };
              if (!isFieldVisible(field, rowValues)) return null;
              return (
                <FieldGroup
                  key={field.id || field.field_key}
                  field={field}
                  value={(row.values || {})[field.field_key]}
                  onChange={(v) => onRowValueChange(row.id, field.field_key, v)}
                  mode={mode}
                  readOnly={readOnly}
                  files={(filesByRow?.[row.id] || {})[field.field_key]}
                  onUploadFile={onUploadRowFile ? (file) => onUploadRowFile(row.id, field, file) : undefined}
                  onDeleteFile={onDeleteFile}
                  onViewFile={onViewFile}
                  allValues={rowValues}
                  siblingFields={fields}
                />
              );
            })}
          </Card.Body>
        </Card>
      ))}
      {!readOnly && (
        <Button size="sm" variant="outline-primary" disabled={atMax} onClick={onAddRow}>+ Add row</Button>
      )}
    </div>
  );
}

export default function DynamicFormRenderer({
  sections = [],
  mode = "preview",
  // ---- fill-mode props (all optional; preview behavior is unchanged if omitted) ----
  values: controlledValues,
  onValueChange,
  repeatingRowsBySection = {},
  onRepeatingRowValueChange,
  onAddRepeatingRow,
  onRemoveRepeatingRow,
  filesByFieldKey = {},
  filesByRow = {},
  onUploadFile,
  onUploadRowFile,
  onDeleteFile,
  onViewFile,
  fieldErrors = {},
  readOnly = false,
  executionProfile = null,
  // Opt-in only (undefined/null for every existing caller -- FormPreview,
  // FormSubmissionViewPage -- so their PROJECT_SELECT/PROJECT_MODULE_SELECT
  // dropdowns render exactly as before). When a caller passes this, those
  // two field types are skipped entirely from the rendered question list --
  // their value already comes from the assignment (seeded by the caller
  // before render), never from a picker here, so showing a dropdown for
  // them would just be a redundant, editable duplicate of context the
  // caller already displays elsewhere (e.g. an "Assignment Details" block).
  assignmentProjectContext = null,
  // Knowledge Sharing subject-selection hardening -- opt-in only (undefined
  // for every existing caller, so an is_subject_field EMPLOYEE_SELECT field
  // anywhere else keeps rendering exactly as before: the plain numeric-id
  // text box). Shape: { submissionId, locked, subject: {user_id,name,email}|null } --
  // see pages/forms/FormFillContent.jsx for how it's built from the fill
  // runtime payload (`runtime.subject`/`runtime.subject_locked`).
  subjectContext = null,
}) {
  const [internalValues, setInternalValues] = useState({});
  const isControlled = mode === "fill" && controlledValues !== undefined;
  const values = isControlled ? controlledValues : internalValues;

  const sortedSections = useMemo(
    () => [...sections].filter((s) => s.is_active !== 0).sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [sections]
  );

  const setFieldValue = (fieldKey, value) => {
    if (isControlled) {
      onValueChange?.(fieldKey, value);
    } else {
      setInternalValues((prev) => ({ ...prev, [fieldKey]: value }));
    }
  };

  return (
    <div className="dynamic-form-renderer">
      {mode === "preview" && (
        <Badge bg="warning" text="dark" className="mb-3">Preview mode — nothing entered here is saved</Badge>
      )}
      {mode === "fill" && readOnly && (
        <div className="dfr-readonly-banner" role="status">
          <i className="fa-solid fa-lock" aria-hidden="true"></i>
          <span>This response has been submitted and is now read-only.</span>
        </div>
      )}

      {sortedSections.length === 0 && <div className="text-muted">This version has no sections yet.</div>}

      {sortedSections.map((section) => {
        const sectionFields = [...(section.fields || [])]
          .filter((f) => f.is_active !== 0)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

        // PROJECT_SELECT/PROJECT_MODULE_SELECT are skipped only when the
        // caller opted in via assignmentProjectContext -- see prop comment.
        const visibleFields = sectionFields.filter((field) => {
          if (!assignmentProjectContext) return true;
          return field.field_type !== "PROJECT_SELECT" && field.field_type !== "PROJECT_MODULE_SELECT";
        });
        let questionCounter = 0;

        return (
          <Card key={section.id} className="mb-3 dfr-section-card">
            <Card.Body>
              <Card.Title className="h6">
                {section.title}
                {!!section.is_repeatable && <Badge bg="light" text="dark" className="ms-2">Repeatable</Badge>}
              </Card.Title>
              {section.description && <Card.Text className="text-muted small">{section.description}</Card.Text>}

              {section.is_repeatable ? (
                <RepeatingSectionRows
                  section={section}
                  fields={sectionFields}
                  rows={repeatingRowsBySection[section.id]}
                  parentValues={values}
                  mode={mode}
                  readOnly={readOnly}
                  filesByRow={filesByRow}
                  onRowValueChange={(rowId, fieldKey, value) => onRepeatingRowValueChange?.(section.id, rowId, fieldKey, value)}
                  onAddRow={() => onAddRepeatingRow?.(section.id)}
                  onRemoveRow={(rowId) => onRemoveRepeatingRow?.(section.id, rowId)}
                  onUploadRowFile={onUploadRowFile}
                  onDeleteFile={onDeleteFile}
                  onViewFile={onViewFile}
                  executionProfile={executionProfile}
                />
              ) : (
                visibleFields.map((field) => {
                  if (!isFieldVisible(field, values)) return null;
                  const isStatic = field.field_type === "SECTION_HEADING" || field.field_type === "STATIC_TEXT";
                  if (!isStatic) questionCounter += 1;
                  return (
                    <FieldGroup
                      key={field.id || field.field_key}
                      field={field}
                      value={values[field.field_key]}
                      onChange={(v) => setFieldValue(field.field_key, v)}
                      mode={mode}
                      readOnly={readOnly}
                      files={filesByFieldKey[field.field_key]}
                      onUploadFile={onUploadFile ? (file) => onUploadFile(field, file) : undefined}
                      onDeleteFile={onDeleteFile}
                      onViewFile={onViewFile}
                      error={fieldErrors[field.field_key]}
                      allValues={values}
                      siblingFields={sectionFields}
                      questionNumber={isStatic ? null : questionCounter}
                      groupId={`ff-field-${field.field_key}`}
                      subjectContext={subjectContext}
                    />
                  );
                })
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}
